import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { FlujoFondosConfig } from '../../../services/tesoreriaFlujoFondosService';

interface Props {
  open: boolean;
  config: FlujoFondosConfig | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (config: FlujoFondosConfig, paraUsuario: boolean) => void;
}

const FlujoFondosConfigDialog: React.FC<Props> = ({ open, config, saving, onClose, onSave }) => {
  const [draft, setDraft] = useState<FlujoFondosConfig | null>(null);

  useEffect(() => {
    if (open && config) setDraft({ ...config });
  }, [open, config]);

  if (!draft) return null;

  const setBool = (key: keyof FlujoFondosConfig, value: boolean) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const setNum = (key: keyof FlujoFondosConfig, value: number) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Configurar fuentes del flujo</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Fuentes firmes
        </Typography>
        <Stack spacing={0.5} mb={2}>
          <FormControlLabel
            control={<Switch checked={draft.incluirSaldos} onChange={(_, v) => setBool('incluirSaldos', v)} />}
            label="Saldo inicial líquido (bancos / cajas, sin cheques)"
          />
          <FormControlLabel
            control={<Switch checked={draft.incluirCxc} onChange={(_, v) => setBool('incluirCxc', v)} />}
            label="Cuentas por cobrar"
          />
          <FormControlLabel
            control={<Switch checked={draft.incluirCxp} onChange={(_, v) => setBool('incluirCxp', v)} />}
            label="Cuentas por pagar"
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.incluirChequesTerceros}
                onChange={(_, v) => setBool('incluirChequesTerceros', v)}
              />
            }
            label="Cheques de terceros en cartera"
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.incluirChequesPropios}
                onChange={(_, v) => setBool('incluirChequesPropios', v)}
              />
            }
            label="Cheques propios emitidos"
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Flujos futuros estimados
        </Typography>
        <Stack spacing={0.5} mb={2}>
          <FormControlLabel
            control={<Switch checked={draft.incluirRemitos} onChange={(_, v) => setBool('incluirRemitos', v)} />}
            label="Remitos a clientes no facturados"
          />
          <FormControlLabel
            control={<Switch checked={draft.incluirPedidos} onChange={(_, v) => setBool('incluirPedidos', v)} />}
            label="Pedidos (no remitidos / no facturados)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.incluirOrdenesCompra}
                onChange={(_, v) => setBool('incluirOrdenesCompra', v)}
              />
            }
            label="Órdenes de compra no recibidas"
          />
          <FormControlLabel
            control={
              <Switch checked={draft.incluirRecepciones} onChange={(_, v) => setBool('incluirRecepciones', v)} />
            }
            label="Recepciones de compra no facturadas"
          />
          <FormControlLabel
            control={
              <Switch
                checked={draft.incluirPresupuestados}
                onChange={(_, v) => setBool('incluirPresupuestados', v)}
              />
            }
            label="Flujos presupuestados"
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Offsets (días) cuando no hay plazo del tercero
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2}>
          <TextField
            label="Remitos"
            type="number"
            size="small"
            fullWidth
            value={draft.offsetDiasRemitos}
            onChange={(e) => setNum('offsetDiasRemitos', Math.max(0, Number(e.target.value) || 0))}
          />
          <TextField
            label="Pedidos"
            type="number"
            size="small"
            fullWidth
            value={draft.offsetDiasPedidos}
            onChange={(e) => setNum('offsetDiasPedidos', Math.max(0, Number(e.target.value) || 0))}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2}>
          <TextField
            label="Órdenes de compra"
            type="number"
            size="small"
            fullWidth
            value={draft.offsetDiasOrdenesCompra}
            onChange={(e) => setNum('offsetDiasOrdenesCompra', Math.max(0, Number(e.target.value) || 0))}
          />
          <TextField
            label="Recepciones"
            type="number"
            size="small"
            fullWidth
            value={draft.offsetDiasRecepciones}
            onChange={(e) => setNum('offsetDiasRecepciones', Math.max(0, Number(e.target.value) || 0))}
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Preferencias del informe
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            label="Períodos por defecto"
            type="number"
            size="small"
            fullWidth
            value={draft.periodosDefault}
            onChange={(e) => setNum('periodosDefault', Math.min(52, Math.max(1, Number(e.target.value) || 12)))}
          />
          <TextField
            label="Umbral excedente ($)"
            type="number"
            size="small"
            fullWidth
            value={draft.umbralExcedente}
            onChange={(e) => setNum('umbralExcedente', Math.max(0, Number(e.target.value) || 0))}
            helperText="Marca períodos con saldo por encima"
          />
        </Stack>
        <Box mt={1}>
          <FormControlLabel
            control={
              <Switch
                checked={draft.vistaDefault === 'M'}
                onChange={(_, v) => setDraft((p) => (p ? { ...p, vistaDefault: v ? 'M' : 'S' } : p))}
              />
            }
            label="Vista mensual por defecto (si no, semanal)"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={!!saving}>
          Cancelar
        </Button>
        <Button variant="outlined" disabled={!!saving} onClick={() => onSave(draft, true)}>
          Guardar para mí
        </Button>
        <Button variant="contained" disabled={!!saving} onClick={() => onSave(draft, false)}>
          Guardar para la empresa
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlujoFondosConfigDialog;
